import formatDate from '../formatDate';

/**
 * Build the legacy `ch.eta` display string — a locale-formatted clock time, or
 * null when the ETA should be hidden.
 *
 * Backward-compatible rule for templates that render `{{ ch.eta }}` directly:
 *   - Hidden (null) when there's no usable estimate or the wait exceeds an hour
 *     (> 60 min) — a far-off ETA is noise; the "More than an hour" band covers it.
 *   - Otherwise the locale-aware time (no seconds). A shown ETA is always ≤ 60
 *     minutes away, i.e. today, so no date is needed.
 *
 * Reuses formatDate's 'time' format so ch.eta matches the region-aware locale
 * handling used everywhere else. Pure / side-effect free for unit testing; the
 * CrowdHandler composable calls it from the `eta` watcher.
 *
 * @param estimateMinutes  Minutes of wait remaining (`ch.estimate`), or null.
 * @param etaIso           The ETA moment as an ISO string (`ch.etaAt`), or null.
 * @param locale           BCP 47 locale (`dateLocale`) to format in.
 * @returns Localised time string, or null when the ETA should not be shown.
 */
export function formatEta(estimateMinutes: number | null, etaIso: string | null, locale: string): string | null {
    if (etaIso == null || estimateMinutes == null || estimateMinutes < 1 || estimateMinutes > 60) {
        return null;
    }
    return formatDate(etaIso, 'time', locale);
}

export default formatEta;
